import React, { useState } from "react";
import { toast } from "sonner";
import { api, IMAGES } from "../lib/site";
import { getUtmSource } from "../lib/utm";

const CATS = ["Hair Care", "Skin Care", "Beard Care", "Accessories", "Self-Care"];

export default function Partnerships() {
  const [form, setForm] = useState({
    brand_name: "", contact_name: "", email: "", website: "", instagram: "",
    category: CATS[0], about: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/partners", { ...form, source: getUtmSource() });
      setDone(true);
      toast.success("Application received", { description: "Our team will review and reach out within 5–7 business days." });
    } catch (err) {
      toast.error("Could not submit", { description: err?.response?.data?.detail || "Please try again." });
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="partnerships-page" className="pt-32">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-2 gap-16 items-center">
        <div>
          <div className="text-[11px] uppercase tracking-luxe text-gold mb-6">Brand Partnerships</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95]">Calling all<br/><span className="italic">Black-Owned Brands.</span></h1>
          <div className="gold-divider my-8" />
          <p className="text-lg text-matte/80 leading-relaxed font-light">
            We're building a curated home for exceptional Black-owned brands in haircare, skincare, beard care, and self-care accessories. If your work is beautiful, intentional, and worthy of a crown — we want to meet you.
          </p>
          <ul className="mt-8 space-y-3 text-matte/75">
            <li className="flex gap-3"><span className="text-gold">◆</span> Direct exposure to our curated waitlist community</li>
            <li className="flex gap-3"><span className="text-gold">◆</span> Editorial spotlight & founder storycards</li>
            <li className="flex gap-3"><span className="text-gold">◆</span> Cross-channel promotion at box launch</li>
            <li className="flex gap-3"><span className="text-gold">◆</span> A partner that grows with you</li>
          </ul>
        </div>
        <div className="relative aspect-[4/5] overflow-hidden">
          <img src={IMAGES.products} alt="Partner with us" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </section>

      <section className="bg-matte text-ivory py-20 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-[11px] uppercase tracking-luxe text-gold mb-4">Application</div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">Become a partner.</h2>
          <p className="mt-4 text-ivory/60 font-light">Tell us about your brand — we personally review every submission.</p>

          {done ? (
            <div data-testid="partner-success" className="mt-12 border border-gold p-10">
              <div className="text-[11px] uppercase tracking-luxe text-gold">Received</div>
              <div className="mt-3 font-serif text-3xl">Thank you for reaching out.</div>
              <p className="mt-3 text-ivory/70">We'll review your submission with care and follow up within 5–7 business days at the email you provided.</p>
            </div>
          ) : (
            <form data-testid="partner-form" onSubmit={submit} className="mt-12 space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <Field label="Brand Name" required value={form.brand_name} onChange={upd("brand_name")} testId="partner-brand" />
                <Field label="Contact Name" required value={form.contact_name} onChange={upd("contact_name")} testId="partner-contact" />
                <Field label="Email" type="email" required value={form.email} onChange={upd("email")} testId="partner-email" />
                <Field label="Website" placeholder="https://" value={form.website} onChange={upd("website")} testId="partner-website" />
                <Field label="Instagram" placeholder="@handle" value={form.instagram} onChange={upd("instagram")} testId="partner-instagram" />
                <div>
                  <label className="block text-[10px] uppercase tracking-luxe-sm text-ivory/50 mb-2">Category</label>
                  <select
                    data-testid="partner-category"
                    value={form.category}
                    onChange={upd("category")}
                    className="w-full bg-transparent border-b border-ivory/30 py-3 outline-none focus:border-gold text-ivory"
                  >
                    {CATS.map(c => <option key={c} className="bg-matte text-ivory">{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-luxe-sm text-ivory/50 mb-2">About Your Brand</label>
                <textarea
                  data-testid="partner-about"
                  required
                  rows={5}
                  value={form.about}
                  onChange={upd("about")}
                  placeholder="Tell us about your mission, products, and what makes your brand exceptional."
                  className="w-full bg-transparent border-b border-ivory/30 py-3 outline-none focus:border-gold text-ivory placeholder:text-ivory/30"
                />
              </div>
              <button
                data-testid="partner-submit"
                type="submit"
                disabled={loading}
                className="px-10 py-4 bg-gold text-matte text-[11px] uppercase tracking-luxe-sm hover:bg-gold-soft transition-colors disabled:opacity-50"
              >
                {loading ? "Submitting…" : "Submit Application"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-luxe-sm text-ivory/50 mb-2">{label}{required && <span className="text-gold">*</span>}</label>
      <input
        data-testid={testId}
        required={required}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder || ""}
        className="w-full bg-transparent border-b border-ivory/30 py-3 outline-none focus:border-gold text-ivory placeholder:text-ivory/30"
      />
    </div>
  );
}
